import { IconChevronLeft, IconChevronRight, IconDots } from "@tabler/icons-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { ControlButton } from "@/components/patterns/control-button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

type PaginationItem = number | "start-ellipsis" | "end-ellipsis";

interface DataTablePaginationProps extends React.ComponentProps<"div"> {
  density?: "default" | "compact";
  page: number;
  pageCount: number;
  pageSize?: number;
  pageSizeOptions?: readonly number[];
  summary: React.ReactNode;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export function getPaginationItems(page: number, pageCount: number): PaginationItem[] {
  const safePageCount = Math.max(1, pageCount);
  const safePage = Math.min(Math.max(1, page), safePageCount);

  if (safePageCount <= 7) {
    return Array.from({ length: safePageCount }, (_, index) => index + 1);
  }
  if (safePage <= 3) return [1, 2, 3, "end-ellipsis", safePageCount];
  if (safePage >= safePageCount - 2) {
    return [1, "start-ellipsis", safePageCount - 2, safePageCount - 1, safePageCount];
  }
  return [1, "start-ellipsis", safePage - 1, safePage, safePage + 1, "end-ellipsis", safePageCount];
}

/** Paper's shared table-footer pagination pattern. */
export function DataTablePagination({
  density = "default",
  page,
  pageCount,
  pageSize,
  pageSizeOptions = [10, 20, 50],
  summary,
  onPageChange,
  onPageSizeChange,
  className,
  "aria-label": ariaLabel,
  ...props
}: DataTablePaginationProps): React.ReactElement {
  const { t } = useTranslation("common");
  const navLabel = ariaLabel ?? t("pagination.tableLabel");
  const safePageCount = Math.max(1, pageCount);
  const safePage = Math.min(Math.max(1, page), safePageCount);
  const normalizedOptions = [...new Set(pageSizeOptions.filter((option) => option > 0))];
  const showPageSize =
    pageSize !== undefined && onPageSizeChange !== undefined && normalizedOptions.length > 0;
  const selectItems = normalizedOptions.map((option) => ({
    label: String(option),
    value: String(option),
  }));

  return (
    <div className={cn("flex w-full items-center justify-between gap-4", className)} {...props}>
      <span
        data-slot="table-pagination-summary"
        className="min-w-0 flex-1 truncate text-[11px] leading-3.5 text-muted-foreground"
      >
        {summary}
      </span>
      <div data-slot="table-pagination-controls" className="flex shrink-0 items-center gap-3">
        {showPageSize ? (
          <div className="flex shrink-0 items-center gap-2">
            <span className="whitespace-nowrap text-[11px] leading-3.5 text-muted-foreground">
              {t("pagination.rowsPerPage")}
            </span>
            <Select
              items={selectItems}
              value={String(pageSize)}
              onValueChange={(next) => {
                if (!next) return;
                const nextSize = Number(next);
                if (normalizedOptions.includes(nextSize)) onPageSizeChange(nextSize);
              }}
            >
              <SelectTrigger
                size="compact"
                aria-label={t("pagination.rowsPerPage")}
                className="min-w-[58px]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="start" alignItemWithTrigger={false} className="min-w-[76px]">
                <SelectGroup>
                  {selectItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        ) : null}
        <nav aria-label={navLabel} className="flex shrink-0 items-center justify-end gap-1">
          <ControlButton
            aria-label={t("pagination.previousPage")}
            controlSize={density === "compact" ? "icon-row" : "compact"}
            variant="ghost"
            className="disabled:bg-transparent disabled:text-ring disabled:opacity-100"
            disabled={safePage <= 1}
            onClick={() => onPageChange(safePage - 1)}
          >
            <IconChevronLeft />
            {density === "default" ? <span>{t("pagination.previous")}</span> : null}
          </ControlButton>
          {getPaginationItems(safePage, safePageCount).map((item) =>
            typeof item === "number" ? (
              <ControlButton
                key={item}
                aria-label={t("pagination.goToPage", { page: item })}
                aria-current={item === safePage ? "page" : undefined}
                controlSize={density === "compact" ? "icon-row" : "icon-compact"}
                variant={item === safePage ? "outline" : "ghost"}
                onClick={() => onPageChange(item)}
              >
                {item}
              </ControlButton>
            ) : (
              <span
                key={item}
                data-pagination-item={item}
                className="flex size-7 items-center justify-center text-muted-foreground"
                aria-hidden="true"
              >
                <IconDots className="size-3.5" />
              </span>
            ),
          )}
          <ControlButton
            aria-label={t("pagination.nextPage")}
            controlSize={density === "compact" ? "icon-row" : "compact"}
            variant="ghost"
            className="disabled:bg-transparent disabled:text-ring disabled:opacity-100"
            disabled={safePage >= safePageCount}
            onClick={() => onPageChange(safePage + 1)}
          >
            {density === "default" ? <span>{t("pagination.next")}</span> : null}
            <IconChevronRight />
          </ControlButton>
        </nav>
      </div>
    </div>
  );
}
